import React, { useEffect, useState } from "react";
import axiosInstance from "../axios/axios";
import dateIcon from "../assets/dateIcon.svg";
import clockIcon from "../assets/clockIcon.svg";
import confirmIcon from "../assets/confirmIcon.svg";
import { Link } from "react-router-dom";
import SuccessMessage from "./SuccessMessage";
import ErrorMessage from "./ErrorMessage";

const StudentNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const userId = localStorage.getItem("user_id");
  const [selectedDates, setSelectedDates] = useState({});
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    if (!userId) {
      setError("Please log in.");
      setLoading(false);
      return;
    }

    const fetchInterviews = async () => {
      try {
        const response = await axiosInstance.get(
          `user-applications-job/${userId}?status=Interview`
        );
        setNotifications(response.data.applications.data || []);
        setError(null);
      } catch (error) {
        console.error("Error fetching interviews:", error);
        setError("Failed to load notifications. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchInterviews();
  }, [userId]);

  const handleAlternativeDateClick = (notificationId, name) => {
    setSelectedDates((prevState) => ({
      ...prevState,
      [notificationId]: name,
    }));
  };

  const handleConfirm = async (interviewId, notificationId) => {
    const selectedDateName = selectedDates[notificationId];

    if (!selectedDateName) {
      setError("Please select an alternative date before confirming.");
      return;
    }

    setConfirming(interviewId);
    setError(null);

    const data = {
      selected_date: selectedDateName,
    };

    try {
      const response = await axiosInstance.patch(
        `interview/${interviewId}/update-date`,
        data
      );
      console.log("Date successfully updated:", response.data);
      setSuccess("Alternative date confirmed successfully!");
    } catch (error) {
      console.error("Error updating the date:", error);
      setError("Failed to confirm the alternative date. Please try again.");
    } finally {
      setConfirming(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-4">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
            {/* Interview Notifications */}
          </h1>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6">
        {/* Success/Error Messages */}
        {success && <SuccessMessage message={success} />}
        {error && <ErrorMessage message={error} />}

        {/* Notifications Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {notifications.length > 0 ? (
            notifications.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-300"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200 rounded-t-xl">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Interview Scheduled - {item.job.job_title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {item.organization.company_name}
                  </p>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-4">
                    <p className="text-gray-700 mb-2">
                      <span className="font-medium">Dear {item.student_name},</span>
                    </p>
                    <p className="text-gray-700 leading-relaxed text-sm">
                      We are pleased to inform you that your interview for the{" "}
                      <span className="font-medium">{item.job.job_title}</span> position at{" "}
                      <span className="font-medium">{item.organization.company_name}</span> has been scheduled.
                    </p>
                  </div>

                  {item.interview ? (
                    <>
                      {/* Main Interview Details */}
                      <div className="bg-green-50 rounded-lg p-4 mb-6 border border-green-200">
                        <h4 className="font-semibold text-gray-900 mb-3 text-sm">Interview Details</h4>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <img src={dateIcon} alt="Date" className="w-4 h-4" />
                            <span className="text-sm text-gray-700">
                              {formatDate(item.interview.date)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <img src={clockIcon} alt="Time" className="w-4 h-4" />
                            <span className="text-sm text-gray-700">
                              {formatTime(item.interview.date)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            <Link 
                              to={item.interview.meet_link}
                              className="text-sm text-green-600 hover:text-green-800 underline font-medium"
                            >
                              Join Meeting
                            </Link>
                          </div>
                        </div>
                      </div>

                      {/* Alternative Dates */}
                      {(item.interview.alt_date_1 || item.interview.alt_date_2) && (
                        <div>
                          <p className="text-gray-700 mb-4 text-sm">
                            Should this date not be convenient, please choose one of the alternative dates below:
                          </p>
                          
                          <div className="grid gap-3 mb-6">
                            {[
                              { name: "alt_date_1", date: item.interview.alt_date_1 },
                              { name: "alt_date_2", date: item.interview.alt_date_2 }
                            ]
                              .filter(altDate => altDate.date)
                              .map((altDate, index) => (
                                <div
                                  key={index}
                                  className={`p-3 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                                    selectedDates[item.id] === altDate.name
                                      ? "border-green-500 bg-green-50 ring-2 ring-green-200"
                                      : "border-gray-200 bg-white hover:border-green-300 hover:bg-green-50"
                                  }`}
                                  onClick={() => handleAlternativeDateClick(item.id, altDate.name)}
                                >
                                  <div className="flex justify-between items-center">
                                    <div className="flex gap-4">
                                      <div className="flex items-center gap-1">
                                        <img src={dateIcon} alt="Date" className="w-4 h-4" />
                                        <span className="text-sm font-medium text-gray-900">
                                          {formatDate(altDate.date)}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <img src={clockIcon} alt="Time" className="w-4 h-4" />
                                        <span className="text-sm text-gray-600">
                                          {formatTime(altDate.date)}
                                        </span>
                                      </div>
                                    </div>
                                    {selectedDates[item.id] === altDate.name && (
                                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>

                          <button
                            onClick={() => handleConfirm(item.interview.id, item.id)}
                            disabled={!selectedDates[item.id] || confirming === item.interview.id}
                            className={`w-full sm:w-auto px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm ${
                              selectedDates[item.id] && confirming !== item.interview.id
                                ? "bg-green-600 hover:bg-green-700 text-white shadow-sm"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                            }`}
                          >
                            {confirming === item.interview.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Confirming...
                              </>
                            ) : (
                              <>
                                <img src={confirmIcon} alt="Confirm" className="w-4 h-4" />
                                Confirm Alternative Date
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-yellow-800 text-sm">
                          Your interview details are not yet available. Please check back later.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full">
              <div className="text-center py-12">
                <div className="mb-4">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-5 5-5-5h5V3h5v14z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No notifications available
                </h3>
                <p className="text-gray-500">
                  You don't have any interview notifications at the moment. Check back later for updates.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentNotifications;